// @flow
import _ from 'lodash';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
// Components
import { Link } from 'react-router-dom';
import CartChangeNotice, {
  pluralizeProducts,
} from '../components/CartChangeNotice';
import CartIndicatorButton from '../components/CartIndicatorButton';
// Composers
import { connect } from 'react-redux';
// Selectors
import { getBlends } from '../../blend/actions';
import { blendsByOrderNumberSelector } from '../../blend/selectors';
import {
  allItemsSelector,
  gettingOrderLoadingSelector,
  itemCountSelector,
  itemTotalSumSelector,
  orderNumberSelector,
} from '../selectors';
// Actions
import { deleteItem, findOrCreateOrder } from '../actions';
// Analytics
import * as Analytics from '../../common/analytics';
// Helpers
import { priceConverter } from '../../common';
import { computeCartChanges } from '../utils/computeCartChanges';
import { toSimpleLineItem } from '../utils/toSimpleLineItem';
// Types
import type { Blend } from '../../blend/types/Blend';
import type { LineItem } from '../types';
// Styles
import './CartIndicatorBottomSheet.css';

// How long the closed sheet signals that the cart was just updated
const CART_UPDATED_DURATION = 10000;

type Props = {
  itemCount: number,
  lineItems: LineItem[],
  orderNumber: ?string,
  loadingOrder: boolean,
  subtotal: number,
  blends: (orderNumber: ?string) => Blend[],
  findOrCreateOrder: () => void,
  getBlends: (orderNumber: string) => void,
};

const isDesktopWidth = () => window.innerWidth > 768;

const CartIndicatorBottomSheet = (props: Props) => {
  const timerRef = useRef(null);
  const prevLineItemsRef = useRef(props.lineItems);
  const prevOrderNumberRef = useRef(props.orderNumber);
  const location = useLocation();

  // Whether the cart was recently changed, signaled while the sheet is closed
  const [cartUpdated, setCartUpdated] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(isDesktopWidth());

  const isDisabledScreenShowCart =
    location.pathname.includes('checkout') ||
    location.pathname.includes('carrinho') ||
    location.pathname.includes('login');
  const isHidden = isDesktop || isDisabledScreenShowCart;
  const isExpanded = expanded && !isHidden && props.itemCount > 0;
  const blends = props.blends(props.orderNumber);

  const cartItems = useMemo(
    () => props.lineItems.map(toSimpleLineItem),
    [props.lineItems],
  );

  useEffect(() => {
    props.findOrCreateOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(isDesktopWidth());

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timerRef.current);
    };
  }, []);

  // Navigating (ex: clicking "Ver carrinho") closes the sheet
  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  // Otherwise the sheet would reopen by itself when it can be shown again (ex: an item added after removing the last one)
  useEffect(() => {
    if (isHidden || props.itemCount <= 0) {
      setExpanded(false);
    }
  }, [isHidden, props.itemCount]);

  // Keep the page behind the open sheet from scrolling
  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isExpanded]);

  useEffect(() => {
    const prevLineItems = prevLineItemsRef.current;
    const prevOrderNumber = prevOrderNumberRef.current;
    prevLineItemsRef.current = props.lineItems;
    prevOrderNumberRef.current = props.orderNumber;

    // If the order was just loaded (ex: Data from server just returned from GET) we should not check for changes
    if (!prevOrderNumber) {
      return;
    }

    // Changes made where the sheet is not shown (ex: cart page) should not be signaled
    if (isDisabledScreenShowCart) {
      setCartUpdated(false);

      return;
    }

    // This protects against the signal being removed due to the final action of an optimistic update.
    if (_.isEqual(prevLineItems, props.lineItems)) {
      return;
    }

    // Changes made from the open sheet are already visible in it
    if (isExpanded) {
      return;
    }

    const changedLineItems = computeCartChanges(prevLineItems, props.lineItems);

    if (changedLineItems.length !== 0) {
      setCartUpdated(true);

      const hasMix = changedLineItems.some(l => l.mixId);
      if (hasMix && props.orderNumber) {
        props.getBlends(props.orderNumber);
      }

      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        () => setCartUpdated(false),
        CART_UPDATED_DURATION,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orderNumber, props.lineItems, location.pathname]);

  // Blends are only fetched when a mix changes, so the sheet may need to load them
  const loadMissingBlends = () => {
    const loadedBlendIds = new Set(blends.map(blend => blend.id));
    const hasMissingBlend = props.lineItems.some(
      lineItem => lineItem.mixId && !loadedBlendIds.has(lineItem.mixId),
    );

    if (hasMissingBlend && props.orderNumber) {
      props.getBlends(props.orderNumber);
    }
  };

  const handleOpen = () => {
    loadMissingBlends();
    clearTimeout(timerRef.current);
    setCartUpdated(false);
    setExpanded(true);
  };

  const handleClose = () => {
    setExpanded(false);
  };

  const handleRemoveItem = (lineItemId: number) => {
    const lineItem = _.find(props.lineItems, item => item.id === lineItemId);
    Analytics.logLineItemRemove(
      lineItem,
      window.location.pathname,
      'cart preview',
    );
    return props.removeItem(lineItemId);
  };

  if (isHidden) {
    return null;
  }

  if (props.itemCount <= 0) {
    return (
      <div className="cart-indicator-bottom-sheet-main">
        <Link to="/carrinho/" className="cart-indicator-bottom-sheet-link">
          <CartIndicatorButton
            quantity={props.itemCount}
            loading={props.loadingOrder}
          />
        </Link>
      </div>
    );
  }

  if (isExpanded) {
    return (
      <>
        <div
          className="cart-indicator-bottom-sheet-backdrop"
          onClick={handleClose}
        />
        <div className="cart-indicator-bottom-sheet-main cart-indicator-bottom-sheet-main--expanded">
          <CartChangeNotice
            variant="cart"
            placement="sheet"
            cartChanges={cartItems}
            blends={blends}
            itemCount={props.itemCount}
            subtotal={props.subtotal}
            onDismiss={handleClose}
            onRemoveItem={handleRemoveItem}
          />
        </div>
      </>
    );
  }

  return (
    <div className="cart-indicator-bottom-sheet-main">
      <div className="cart-indicator-bottom-sheet-bar">
        <button
          type="button"
          className="cart-indicator-bottom-sheet-summary"
          onClick={handleOpen}
          aria-haspopup="dialog"
          aria-expanded={false}
        >
          {cartUpdated ? (
            <i
              className="fa fa-check-circle cart-indicator-bottom-sheet-updated-icon"
              aria-hidden="true"
            />
          ) : (
            <div className="cart-indicator-button-icon" aria-hidden="true" />
          )}
          <div className="cart-indicator-bottom-sheet-summary-text">
            <strong
              className={`cart-indicator-bottom-sheet-summary-title ${cartUpdated ? 'cart-indicator-bottom-sheet-summary-title--updated' : ''}`}
            >
              {cartUpdated ? 'Carrinho atualizado' : 'Carrinho'}
            </strong>
            <span className="cart-indicator-bottom-sheet-summary-detail">
              {pluralizeProducts(props.itemCount)} ·{' '}
              {priceConverter(props.subtotal)}
            </span>
          </div>
          <i
            className="fa fa-chevron-up cart-indicator-bottom-sheet-chevron"
            aria-hidden="true"
          />
        </button>

        <Link to="/carrinho/" className="cart-indicator-bottom-sheet-button">
          Ver carrinho
        </Link>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    itemCount: itemCountSelector(state),
    lineItems: allItemsSelector(state),
    orderNumber: orderNumberSelector(state),
    loadingOrder: gettingOrderLoadingSelector(state),
    subtotal: itemTotalSumSelector(state),
    blends: orderNumber => blendsByOrderNumberSelector(state, orderNumber),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps) => {
  return {
    findOrCreateOrder() {
      dispatch(findOrCreateOrder());
    },
    getBlends(orderNumber: string) {
      dispatch(getBlends(orderNumber));
    },
    removeItem(lineItemId: number) {
      return dispatch(deleteItem(lineItemId));
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(CartIndicatorBottomSheet);
